import { Breadcrumb, BreadcrumbItem, Button, Card, Label, Textarea, TextInput } from "flowbite-react";
import * as Sentry from "@sentry/react";
import type { FC } from "react";
import {
  sendNotificationToUser,
  broadcastNotification,
} from "../../services/notifications";
import { NotificationType } from "../../types/notifications";
import { HiHome } from "react-icons/hi";
import { useState } from "react";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import { ToastContainer, toast } from "react-toastify";
import UserSearchSelect from "../../components/UserSearchSelect";

const NotificationsPage: FC = function () {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notificationMode, setNotificationMode] = useState<
    "user" | "broadcast"
  >("user");
  const [notification, setNotification] = useState({
    userId: "",
    title: "",
    body: "",
    data: {},
    type: NotificationType.GENERIC,
  });
  const [selectedUser, setSelectedUser] = useState<any>(null);

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    if (name === "userId") {
      setNotification((prev: any) => ({
        ...prev,
        [name]: value,
      }));
    } else {
      setNotification((prev: any) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleUserChange = (selectedOption: any) => {
    setSelectedUser(selectedOption);
    setNotification((prev: any) => ({
      ...prev,
      userId: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      let response;
      if (notificationMode === "user") {
        if (!selectedUser) {
          toast.error("Please select a user for individual notifications");
          return;
        }
        response = await sendNotificationToUser({
          userId: notification.userId,
          title: notification.title,
          body: notification.body,
          data: notification.data,
          type: notification.type,
        });
      } else {
        response = await broadcastNotification({
          title: notification.title,
          body: notification.body,
          data: notification.data,
          type: notification.type,
        });
      }

      if (response.data.error) {
        toast.error(response.data.message);
        Sentry.captureMessage("Error sending notification", {
          extra: {
            notification,
            mode: notificationMode,
          },
        });
      } else {
        if (notificationMode === "user") {
          toast.success("Notification has been sent successfully!");
        } else {
          const broadcastResults = response.data.results as
            | { totalEligible: number; queuedCount: number }
            | undefined;
          if (broadcastResults) {
            toast.success(
              `Broadcast notification queued: ${broadcastResults.queuedCount}/${broadcastResults.totalEligible} notifications will be sent`,
            );
          } else {
            toast.success("Broadcast notification queued successfully!");
          }
        }
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
      Sentry.captureMessage("Error sending notification", {
        extra: {
          notification,
          mode: notificationMode,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <NavbarSidebarLayout isFooter={false}>
      <div className="p-4">
        <div className="max-w-6xl">
          <div className="mb-3">
            <Breadcrumb className="mb-3">
              <BreadcrumbItem href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">Home</span>
                </div>
              </BreadcrumbItem>
              <BreadcrumbItem href="/notifications">
                Notifications
              </BreadcrumbItem>
            </Breadcrumb>
          </div>
          <div className="mb-4 block text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
            Notifications
          </div>

          <Card className="p-4 max-w-2xl">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                  Send Push Notification
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  Send notifications to specific users or broadcast to all
                  users.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Notification Type</Label>
                  <div className="mt-2 flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="notificationMode"
                        value="user"
                        checked={notificationMode === "user"}
                        onChange={(e) =>
                          setNotificationMode(
                            e.target.value as "user" | "broadcast",
                          )
                        }
                        className="mr-2"
                      />
                      Send to Specific User
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="notificationMode"
                        value="broadcast"
                        checked={notificationMode === "broadcast"}
                        onChange={(e) =>
                          setNotificationMode(
                            e.target.value as "user" | "broadcast",
                          )
                        }
                        className="mr-2"
                      />
                      Broadcast to All Users
                    </label>
                  </div>
                </div>
                {notificationMode === "user" && (
                  <div>
                    <Label htmlFor="userId">User</Label>
                    <div className="mt-2">
                      <UserSearchSelect
                        value={selectedUser}
                        onChange={handleUserChange}
                      />
                    </div>
                  </div>
                )}
                <div>
                  <Label htmlFor="title">Title</Label>
                  <div className="mt-2">
                    <TextInput
                      id="title"
                      name="title"
                      placeholder="Title"
                      onChange={handleChange}
                      value={notification.title}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label>{"Message"}</Label>
                  <div className="mt-2">
                    <Textarea
                      id="body"
                      name="body"
                      placeholder="Message"
                      onChange={handleChange}
                      value={notification.body}
                      required
                    />
                  </div>
                </div>
                <Button
                  color="primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Sending..."
                    : notificationMode === "broadcast"
                      ? "Broadcast to All"
                      : "Send to User"}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        hideProgressBar
        newestOnTop={false}
        rtl={false}
        pauseOnFocusLoss
        pauseOnHover
        theme="dark"
      />
    </NavbarSidebarLayout>
  );
};

export default NotificationsPage;
